import React, { FC, useState } from 'react';

import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { getCollectionLoanFormats } from '../../store/loanFormat/actions';
import NavigationDropDownLink from '../components/NavigationDropDownLink';
import { LoanFormatState } from '../../store/loanFormat/state';
import { DropDownMenu } from '../common/DropDownMenu';
import { AppState } from '../../store/appState';
import './LoanFormatDropDown.scss';
import { LoanFormatIcon } from '../common/GildIcon';
import { modifyURLState } from '../utils/utils-routing';
import { deriveSearchScopeFromPathname } from '../search/utils/deriveSearchScopeFromPathname';
import { useTranslation } from 'react-i18next';
import { Location } from 'history';

type Props = {
    readonly showIcons?: boolean;
};

const SHOP_PATH_PREFIX = '/shop/';

export const createLoanFormatLink = (
    location: Location<unknown>,
    currentLoanFormatKey: string | undefined = '',
    loanFormatKey: string | undefined = ''
) => {
    loanFormatKey = loanFormatKey.toLowerCase();
    currentLoanFormatKey = currentLoanFormatKey.toLowerCase();

    let pathname = location.pathname;
    let isShopPath = pathname.startsWith(SHOP_PATH_PREFIX);

    // NOTE:
    //  We have to prevent, that the following replace function replaces the shop language "all"
    //  with the desired loan format key.
    pathname = (isShopPath ? pathname.substring(SHOP_PATH_PREFIX.length) : pathname).replace(
        new RegExp(`(/)(${currentLoanFormatKey})(/|$)`, 'gi'),
        `$1${loanFormatKey}$3`
    );

    const search = modifyURLState(location.search, 'page', undefined, false);

    return `${isShopPath ? SHOP_PATH_PREFIX : ''}${pathname}?${search}`;
};

const LoanFormatDropDown: FC<Props> = props => {
    const { t } = useTranslation();
    const location = useLocation();
    const [expanded, setExpanded] = useState(false);
    const loanFormat = deriveSearchScopeFromPathname(location.pathname);
    const currentLoanFormat = useSelector(selectLoanFormat) ?? loanFormat;

    const currentLoanFormatLabel = t(`components.searchScope.collection.${currentLoanFormat.urlKey}`, {
        defaultValue: currentLoanFormat.name
    });

    const loanFormats = getCollectionLoanFormats();

    return (
        <DropDownMenu
            expanded={expanded}
            onToggleClick={setExpanded}
            onClickOutside={setExpanded}
            className={classNames('dd-loanformat', 'fl-none')}
            label={currentLoanFormatLabel}
            iconCode={props.showIcons ? currentLoanFormat.icon : undefined}
            iconDefaultClasses={props.showIcons ? 'bolinda-icons' : undefined}
            dataDisplayname="LoanformatDropdown"
        >
            {loanFormats.map(loanFormat => {
                const to = createLoanFormatLink(location, currentLoanFormat.key, loanFormat.key);

                return (
                    <li key={loanFormat.key}>
                        <NavigationDropDownLink to={to} activeClassName="checked" onClick={() => setExpanded(false)}>
                            {props.showIcons ? (
                                <LoanFormatIcon icon={loanFormat.icon} className="loanformat-icon-medium" />
                            ) : null}
                            {t(`components.loanFormatDropdown.${loanFormat.urlKey}`, {
                                defaultValue: loanFormat.name
                            })}
                            <i className="material-icons check-icon">check</i>
                        </NavigationDropDownLink>
                    </li>
                );
            })}
        </DropDownMenu>
    );
};

export default LoanFormatDropDown;

function selectLoanFormat(state: AppState): LoanFormatState {
    return state.loanFormat;
}
